import { Children } from 'react';

import type { ReactElement } from 'react';
import type { SortableItemProps } from './components/SortableItem';

import { SortableItem } from './components/SortableItem';
import { SortableProvider } from './SortableProvider';

export type Index = string | number;
export type SortableItemColors = 'grey-200' | 'white';

export interface SortableProps<T = unknown> {
  /** The elements to show inside the Sortable. */
  children: ReactElement[] | string[];
  /** The data objects that are actually being sorted. */
  childrenData?: T[];
  /** The placement of each item Handle (6 dots 2x3). */
  ItemHandlePlacement?: SortableItemProps['handlePlacement'];
  /** If true, a scrollable version of the list will be rendered (max height 21rem/336px). */
  scrollable?: boolean;
  /** If true, the sorting will be disabled. */
  disabled?: boolean;
  /** Hide the numbers shown next to the sortable elements. */
  hideNumbers?: boolean;
  /** Change the color of the SortableItem wrapper. */
  sortableItemColor?: SortableItemColors;
  /** The callback function that is invoked when new sorting takes place. */
  onSort?: (items: T[]) => void;
}

// A component that renders a list of children that can be sorted with drag and drop.
export const Sortable = <T,>({
  children,
  childrenData,
  ItemHandlePlacement = 'left',
  scrollable,
  disabled,
  onSort,
  hideNumbers,
  sortableItemColor = 'grey-200',
}: SortableProps<T>) => {
  const sortableData: T[] = childrenData ?? (children as unknown as T[]);

  const wrappedItems = children.map((child, index) => (
    <SortableItem
      handlePlacement={ItemHandlePlacement}
      disabled={disabled || Children.count(children) <= 1}
      sortableItemColor={sortableItemColor}
      index={index}
      key={`sortable-item-${index}`}
    >
      {child}
    </SortableItem>
  ));

  return (
    <SortableProvider
      renderItems={wrappedItems}
      dataItems={sortableData}
      scrollable={scrollable}
      hideNumbers={hideNumbers}
      onSort={onSort}
      key={`sortable-with-children-${Children.count(children)}`}
    >
      {wrappedItems}
    </SortableProvider>
  );
};
